import { createSlice } from "@reduxjs/toolkit";

import { TapeChartState } from "@/types/store";
import { DEFAULT_BEDROOM_COUNT } from "@/utils/constants";
import {
  deleteIndividualCalendarBlock,
  getCityStates,
  getTapeChartData,
  updateCalendarBlock,
} from "@/api/calendarBlocks/thunks";
import { TapeChartProperty, TapeChartUnit } from "@/types/tapechart";
import { CalendarBlockFromBE } from "@/types/properties";

const PAGE_SIZE = 10;

export const initialState: TapeChartState = {
  items: {
    properties: [],
    units: [],
  },
  filterStartDate: null,
  filterEndDate: null,
  filterProperties: [],
  filterNumberOfBedrooms: DEFAULT_BEDROOM_COUNT,
  filterHomeowners: [],
  filterCityStates: [],
  filterPropertyTagIds: [],
  loading: false,
  loadingMore: false,
  hasNextPage: true,
  currentPage: 1,
  error: null,
};

const mergeBlocks = (
  existing: CalendarBlockFromBE[] = [],
  incoming: CalendarBlockFromBE[] = []
): CalendarBlockFromBE[] => {
  const map = new Map(existing.map((b) => [b.id, b]));
  for (const b of incoming) map.set(b.id, b);
  return Array.from(map.values());
};

const mergeUnit = (
  existing: TapeChartUnit,
  incoming: TapeChartUnit
): TapeChartUnit => ({
  ...existing,
  ...incoming,
  blocks: mergeBlocks(existing.blocks, incoming.blocks),
});

const mergeUnitsById = (
  existing: TapeChartUnit[],
  incoming: TapeChartUnit[]
): TapeChartUnit[] => {
  const idx = new Map(existing.map((u, i) => [u.id, i]));
  const merged = [...existing];
  for (const u of incoming) {
    const i = idx.get(u.id);
    if (i !== undefined) {
      merged[i] = mergeUnit(merged[i], u);
    } else {
      idx.set(u.id, merged.length);
      merged.push(u);
    }
  }
  return merged;
};

const mergeProperty = (
  existing: TapeChartProperty,
  incoming: TapeChartProperty
): TapeChartProperty => ({
  ...existing,
  ...incoming,
  blocks: mergeBlocks(existing.blocks, incoming.blocks),
  units:
    incoming.units || existing.units
      ? mergeUnitsById(existing.units ?? [], incoming.units ?? [])
      : undefined,
});

const mergePropertiesById = (
  existing: TapeChartProperty[],
  incoming: TapeChartProperty[]
): TapeChartProperty[] => {
  const idx = new Map(existing.map((p, i) => [p.id, i]));
  const merged = [...existing];
  for (const p of incoming) {
    const i = idx.get(p.id);
    if (i !== undefined) {
      merged[i] = mergeProperty(merged[i], p);
    } else {
      idx.set(p.id, merged.length);
      merged.push(p);
    }
  }
  return merged;
};

export const tapeChartSlice = createSlice({
  name: "tapeChartSlice",
  initialState,
  reducers: {
    clearTapeChartData: (state) => {
      Object.assign(state, initialState);
    },
    setStartDate: (state, action) => {
      state.filterStartDate = action.payload;
    },
    setEndDate: (state, action) => {
      state.filterEndDate = action.payload;
    },
    setNumberOfBedrooms: (state, action) => {
      state.filterNumberOfBedrooms = action.payload;
    },
    setFilterProperties: (state, action) => {
      state.filterProperties = action.payload;
    },
    setFilterHomeowners: (state, action) => {
      state.filterHomeowners = action.payload;
    },
    setFilterCityStates: (state, action) => {
      state.filterCityStates = action.payload;
    },
    setFilterPropertyTags: (state, action) => {
      state.filterPropertyTagIds = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    collapseDisplay: (state) => {
      state.loading = true;
      const properties = state.items.properties.slice(0, PAGE_SIZE);
      state.items.properties = properties;
      state.items.units = state.items.units.filter((unit) =>
        properties.some((property) => property.id === unit.propertyId)
      );
      state.currentPage = 1;
      state.hasNextPage = true;
      state.loading = false;
    },
    refreshTapeChartData: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTapeChartData.pending, (state, action) => {
        // Skeleton only for reset (filter apply) or initial empty state;
        // pagination + range-extension keep the grid mounted.
        if (action.meta.arg.reset || state.items.properties.length === 0) {
          state.loading = true;
        } else {
          state.loadingMore = true;
        }
      })
      .addCase(getTapeChartData.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        if (action.meta.arg.reset) {
          state.items = {
            properties: action.payload.properties,
            units: action.payload.units,
          };
        } else {
          state.items = {
            properties: mergePropertiesById(
              state.items.properties,
              action.payload.properties
            ),
            units: mergeUnitsById(state.items.units, action.payload.units),
          };
        }
        state.hasNextPage = action.payload.hasNextPage;
        state.currentPage = action.payload.page || 1;
        state.error = null;
      })
      .addCase(updateCalendarBlock.fulfilled, (state, action) => {
        if (action.payload?.id) {
          state.items.properties = state.items.properties.map((property) => {
            return {
              ...property,
              blocks: property.blocks.map((block: CalendarBlockFromBE) =>
                block.id === action.payload.id ? action.payload : block
              ),
            };
          });
          state.items.units = state.items.units.map((unit) => {
            return {
              ...unit,
              blocks: unit.blocks.map((block: CalendarBlockFromBE) =>
                block.id === action.payload.id ? action.payload : block
              ),
            };
          });
        }
      })
      .addCase(deleteIndividualCalendarBlock.fulfilled, (state, action) => {
        if (action.payload?.blockId) {
          state.items.properties = state.items.properties.map((property) => {
            return {
              ...property,
              blocks: property.blocks.filter(
                (block: CalendarBlockFromBE) =>
                  block.id !== action.payload.blockId
              ),
            };
          });
          state.items.units = state.items.units.map((unit) => {
            return {
              ...unit,
              blocks: unit.blocks.filter(
                (block: CalendarBlockFromBE) =>
                  block.id !== action.payload.blockId
              ),
            };
          });
        }
      })
      .addCase(getTapeChartData.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = action.payload as string;
      })
      .addCase(getCityStates.fulfilled, (state, action) => {
        state.filterCityStates = action.payload;
      });
  },
});

export const {
  clearTapeChartData,
  setCurrentPage,
  setLoading,
  setStartDate,
  setEndDate,
  setNumberOfBedrooms,
  setFilterProperties,
  setFilterHomeowners,
  setFilterCityStates,
  setFilterPropertyTags,
  collapseDisplay,
  refreshTapeChartData,
} = tapeChartSlice.actions;

export default tapeChartSlice.reducer;
